/**
 * API client: X-User-Id injection (embed), 401 auth reset, and 503 retry.
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';
import { embedLog } from '../embed/embedDebug';

// Runtime config (Docker entrypoint injection) → build-time env → fallback
const rc = (window as any).__RUNTIME_CONFIG__ as Record<string, string> | undefined;
const apiUrl = rc?.API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8064';

const apiClient: AxiosInstance = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const userId = useAuthStore.getState().userId;
  if (userId) config.headers['X-User-Id'] = userId;
  return config;
});

// CU-869dne0n5: /api/admin/login и /api/admin/check исключены из 401-reset —
// их 401 штатный ответ (неверный пароль / неавторизован), сбрасывать auth не надо.
const AUTH_EXEMPT_URLS = ['/api/admin/login', '/api/admin/check'];

// 401 auth-reset (CU-869dndzb3 / CU-869dne0n5)
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const store = useAuthStore.getState();
    const url: string = error?.config?.url ?? '';

    // 401 → сброс auth. В embed-режиме hub сам ре-эмитит INIT со свежим
    // токеном при ротации сессии, отдельный refresh-канал не нужен.
    if (
      error.response?.status === 401 &&
      (store.mode === 'embed' || store.mode === 'dashboard') &&
      !AUTH_EXEMPT_URLS.some((exempt) => url.startsWith(exempt))
    ) {
      store.resetAuth();
    }

    return Promise.reject(error);
  },
);

/**
 * CU-869dnpp43: при 503 (БД временно недоступна, save verification failed)
 * автоматически повторяем запрос до 2 раз, уважая Retry-After header.
 * Без этого пользователь видел бы провал и должен был сам нажимать кнопку.
 */
const MAX_RETRIES_ON_503 = 2;
const MAX_RETRY_AFTER_SECONDS = 30;

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryableConfig | undefined;
    if (!config || error.response?.status !== 503) return Promise.reject(error);

    config._retryCount = (config._retryCount ?? 0) + 1;
    if (config._retryCount > MAX_RETRIES_ON_503) return Promise.reject(error);

    const retryAfterHeader = error.response.headers['retry-after'];
    const retryAfterSec = Math.min(
      MAX_RETRY_AFTER_SECONDS,
      Math.max(1, parseInt(String(retryAfterHeader ?? '1'), 10) || 1),
    );

    await new Promise<void>((resolve) => setTimeout(resolve, retryAfterSec * 1000));
    return apiClient.request(config);
  },
);

export default apiClient;

/* ── Keepa types ── */

export interface PackageDimensions {
  length: number | null;
  width: number | null;
  height: number | null;
}

export interface KeepaProductData {
  asin: string;
  buy_box_price: number | null;
  price_source: string | null;
  fba_fee: number | null;
  referral_fee: number | null;
  monthly_sales: number | null;
  package_weight_kg: number | null;
  volumetric_weight_kg: number | null;
  package_dimensions_cm: PackageDimensions | null;
  bsr_data: number[] | null;
  price_history: number[] | null;
  error: string | null;
}

export interface KeepaHistoryPoint {
  x: number;
  y: number;
}

export interface KeepaHistoryResponse {
  asin: string;
  title: string | null;
  price: KeepaHistoryPoint[];
  bsr: KeepaHistoryPoint[];
  window_days: number;
  price_points: number;
  bsr_points: number;
}

export interface MonthlyForecast {
  month: number;
  month_name: string;
  sales: number;
  revenue: number;
  profit: number;
  cumulative_profit: number;
}

export interface ProfitForecastResponse {
  profit_per_unit: number;
  batch_quantity: number;
  batch_profit: number;
  forecast: MonthlyForecast[];
  keepa_data: KeepaProductData | null;
  seasonality: number[] | null;
}

/* ── API functions ── */

export async function fetchKeepaData(
  asins: string[],
  signal?: AbortSignal,
): Promise<Record<string, KeepaProductData>> {
  const { data } = await apiClient.post('/api/keepa-data', { asins }, { signal });
  return data;
}

export async function fetchKeepaHistory(
  asin: string,
  windowDays = 365,
): Promise<KeepaHistoryResponse> {
  const { data } = await apiClient.get('/api/keepa-history', {
    params: { asin, window_days: windowDays },
  });
  return data;
}

export interface CompetitorData {
  asin: string;
  title?: string;
  image?: string;
  price?: number;
  brand?: string;
  rating?: string;
  reviews?: string;
  bsr?: string;
  clickShare?: number;
  monthlySales?: number;
}

export interface NicheData {
  nicheId: string;
  nicheTitle: string;
  products: CompetitorData[];
  count: number;
}

export interface CompetitorsResponse {
  niches: NicheData[];
  competitors: CompetitorData[];
  nicheTitle?: string;
  totalFound?: number;
}

export async function fetchCompetitors(
  asin: string,
): Promise<CompetitorsResponse> {
  const { data } = await apiClient.post('/proxy/competitors', { asin });
  return {
    niches: data.niches ?? [],
    competitors: data.competitors ?? [],
    nicheTitle: data.nicheTitle,
    totalFound: data.totalFound,
  };
}

export async function fetchProfitForecast(params: {
  asin: string;
  competitor_asins?: string[];
  storage_fee_per_unit: number;
  purchase_price: number;
}): Promise<ProfitForecastResponse> {
  const { data } = await apiClient.post(
    '/api/calculate-profit-forecast',
    params,
  );
  return data;
}

/* ── Save / Share / Export ── */

export async function saveResult(
  fileName: string,
  calcData: Record<string, unknown>,
  resultId?: number | null,
): Promise<{ success: boolean; message: string }> {
  const userId = useAuthStore.getState().userId;
  // resultId → правим ИМЕННО эту запись (имя не менялось). Без него — сохранение
  // в своё пространство (owner_id, fileName); смена имени = новая копия.
  embedLog('saveResult → POST /api/save-result', { fileName, resultId: resultId ?? null, userId, parent_asin: calcData?.parent_asin });
  try {
    const { data } = await apiClient.post('/api/save-result', {
      fileName,
      data: calcData,
      resultId: resultId ?? null,
    });
    embedLog('saveResult ✓', data);
    return data;
  } catch (e) {
    const err = e as { response?: { status?: number; data?: unknown }; message?: string };
    embedLog('saveResult ✗', { status: err.response?.status, detail: err.response?.data, message: err.message });
    throw e;
  }
}

/** Загрузка записи по id (однозначно, вкл. чужие для админа/по доступу). */
export async function loadResultById(id: number): Promise<Record<string, unknown>> {
  embedLog('loadResultById → GET /api/saved-results/id/' + id);
  try {
    const { data } = await apiClient.get(`/api/saved-results/id/${id}`);
    embedLog('loadResultById ✓', { id, fileName: data?.fileName });
    return data;
  } catch (e) {
    const err = e as { response?: { status?: number; data?: unknown }; message?: string };
    embedLog('loadResultById ✗', { id, status: err.response?.status, detail: err.response?.data, message: err.message });
    throw e;
  }
}

export async function createShareLink(
  fileName: string,
): Promise<{ share_id: string; url: string }> {
  const { data } = await apiClient.post('/api/share', { fileName });
  return data;
}

export async function fetchSharedResult(
  shareId: string,
): Promise<Record<string, unknown>> {
  embedLog('fetchSharedResult → GET /api/share/' + shareId);
  try {
    const { data } = await apiClient.get(`/api/share/${shareId}`);
    embedLog('fetchSharedResult ✓', { shareId, id: data?.id, fileName: data?.fileName });
    return data;
  } catch (e) {
    const err = e as { response?: { status?: number; data?: unknown }; message?: string };
    embedLog('fetchSharedResult ✗', { shareId, status: err.response?.status, detail: err.response?.data, message: err.message });
    throw e;
  }
}

export async function exportXlsx(
  payload: Record<string, unknown>,
): Promise<Blob> {
  const { data } = await apiClient.post('/api/export-xlsx', payload, {
    responseType: 'blob',
  });
  return data;
}

/* ── History / User Results ── */

export interface HistoryVersion {
  id: number;
  fileName: string;
  parent_asin: string;
  timestamp: string;
  is_variation: boolean;
  variation_idea: string;
  selling_price: number | null;
  monthly_sales: number | null;
  owner_id?: string;
}

export interface HistoryGroup {
  asin: string;
  latest_timestamp: string;
  versions_count: number;
  versions: HistoryVersion[];
}

export async function fetchUserResults(): Promise<{ groups: HistoryGroup[] }> {
  const { data } = await apiClient.get('/api/user-results');
  return data;
}

export async function claimShare(shareId: string): Promise<{ success: boolean }> {
  const { data } = await apiClient.post('/api/claim-share', { share_id: shareId });
  return data;
}

export async function loadSavedResult(filename: string): Promise<Record<string, unknown>> {
  const { data } = await apiClient.get(`/api/saved-results/${filename}`);
  return data;
}
